import { useEffect } from "react";

/**
 * useSEO — manages <title>, meta description, Open Graph, Twitter Cards,
 * canonical URL and an optional JSON-LD structured data block per page.
 * No external dependencies.
 *
 * @param {Object} cfg
 * @param {string} cfg.title              Document title.
 * @param {string} cfg.description        Meta description (≤ 160 chars).
 * @param {string} [cfg.image]            Absolute URL for og:image / twitter:image.
 * @param {string} [cfg.imageAlt]         Alt text for the OG image.
 * @param {string} [cfg.url]              Canonical URL (defaults to current location).
 * @param {string} [cfg.type]             og:type — defaults to "website".
 * @param {string} [cfg.siteName]         og:site_name.
 * @param {string} [cfg.locale]           og:locale — defaults to "pt_PT".
 * @param {string} [cfg.themeColor]       <meta name="theme-color">.
 * @param {Object} [cfg.jsonLd]           JSON-LD object injected as <script type="application/ld+json">.
 */
export function useSEO(cfg) {
  useEffect(() => {
    if (!cfg) return;

    const {
      title,
      description,
      image,
      imageAlt,
      url,
      type = "website",
      siteName = "Tarquínio × BBQ Assador — Costa da Caparica",
      locale = "pt_PT",
      themeColor,
      jsonLd,
    } = cfg;

    const canonicalUrl =
      url || (typeof window !== "undefined" ? window.location.href : "");

    if (title) document.title = title;

    setMetaName("description", description);
    setMetaName("twitter:card", image ? "summary_large_image" : "summary");
    setMetaName("twitter:title", title);
    setMetaName("twitter:description", description);
    if (image) setMetaName("twitter:image", image);
    if (imageAlt) setMetaName("twitter:image:alt", imageAlt);

    setMetaProperty("og:title", title);
    setMetaProperty("og:description", description);
    setMetaProperty("og:type", type);
    setMetaProperty("og:site_name", siteName);
    setMetaProperty("og:locale", locale);
    if (canonicalUrl) setMetaProperty("og:url", canonicalUrl);
    if (image) {
      setMetaProperty("og:image", image);
      setMetaProperty("og:image:width", "1200");
      setMetaProperty("og:image:height", "630");
      if (imageAlt) setMetaProperty("og:image:alt", imageAlt);
    }

    if (themeColor) setMetaName("theme-color", themeColor);

    setLink("canonical", canonicalUrl);

    setJsonLd("page-jsonld", jsonLd);
  }, [cfg]);
}

function setMetaName(name, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(property, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  const existing = document.getElementById(id);
  if (!data) {
    if (existing) existing.remove();
    return;
  }
  const script =
    existing ||
    Object.assign(document.createElement("script"), {
      type: "application/ld+json",
      id,
    });
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}
